import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Moon, Sun, ChevronDown, Home } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const closeTimeoutRef = useRef(null);
  const [aboutClicks, setAboutClicks] = useState(0);
  const aboutResetTimeout = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (aboutResetTimeout.current) {
        clearTimeout(aboutResetTimeout.current);
      }
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleDropdown = (dropdown) => {
    if (isMobile) {
      setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
    }
  };

  const handleMouseEnter = (dropdown) => {
    if (!isMobile) {
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setActiveDropdown(dropdown);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      // Add a delay before closing to allow mouse movement to dropdown
      closeTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 300); // 300ms delay
    }
  };

  const handleDropdownMenuEnter = () => {
    // Clear close timeout when entering dropdown menu
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleDropdownMenuLeave = () => {
    // Close dropdown when leaving menu
    if (!isMobile) {
      closeTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 200);
    }
  };

  // Easter egg: 7 clicks on About redirects to /auth
  const handleAboutClick = (e) => {
    const next = aboutClicks + 1;
    setAboutClicks(next);

  // Reset timer window (8 seconds)
  if (aboutResetTimeout.current) clearTimeout(aboutResetTimeout.current);
  aboutResetTimeout.current = setTimeout(() => setAboutClicks(0), 8000);

    if (next >= 7) {
      e.preventDefault();
      setAboutClicks(0);
      if (aboutResetTimeout.current) {
        clearTimeout(aboutResetTimeout.current);
        aboutResetTimeout.current = null;
      }
      setIsOpen(false);
      setActiveDropdown(null);
      navigate('/auth');
    } else {
      // proceed to About Us as usual
      setIsOpen(false);
      setActiveDropdown(null);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <Link to="/" className="logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_academy-admin-2/artifacts/aa7nbreh_adhva-logo.png" 
            alt="Adhva Academy" 
            className="logo-image"
          />
          <div className="logo-text">
            <span className="logo-title">Adhva Academy</span>
            <span className="logo-tagline">The Learning Path</span>
          </div>
        </Link>
        
        {/* Mobile Header Icons - Home and Theme Toggle */}
        {isMobile && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginLeft: 'auto',
            marginRight: '0'
          }}>
            <Link 
              to="/" 
              onClick={() => setIsOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.375rem',
                color: 'var(--foreground)',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--secondary)';
                e.currentTarget.style.color = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--foreground)';
              }}
              aria-label="Home"
            >
              <Home size={20} />
            </Link>
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: 'var(--foreground)',
                cursor: 'pointer',
                borderRadius: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--secondary)';
                e.currentTarget.style.color = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--foreground)';
              }}
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
          </div>
        )}
        
        <button className="mobile-menu-btn" onClick={() => setIsOpen(!isOpen)} style={isMobile ? { marginLeft: '0.5rem' } : {}}>
          ☰
        </button>

        <ul className={`nav-links ${isOpen ? 'active' : ''}`}>
          <li className={isMobile ? 'nav-link-mobile' : ''}><Link to="/" onClick={() => setIsOpen(false)}>🏠 Home</Link></li>
          
          {/* Learning Paths Dropdown */}
          <li 
            className="dropdown" 
            onMouseEnter={() => handleMouseEnter('learning-paths')} 
            onMouseLeave={handleMouseLeave}
          >
            <button 
              className="dropdown-toggle" 
              onClick={() => toggleDropdown('learning-paths')}
              data-testid="learning-paths-dropdown"
            >
              🎯 Learning Paths <ChevronDown size={16} />
            </button>
            {activeDropdown === 'learning-paths' && (
              <ul 
                className="dropdown-menu"
                onMouseEnter={handleDropdownMenuEnter}
                onMouseLeave={handleDropdownMenuLeave}
              >
                <li><Link to="/adhva-prelims-path-2026" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>🎯 Adhva Prelims Path 2026</Link></li>
                <li><Link to="/adhva-diksoochi-kas-2026" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>🧭 Adhva Diksoochi KAS 2026</Link></li>
                <li><Link to="/adhva-monthly-current-affairs" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>📅 Adhva Monthly Current Affairs</Link></li>
                <li><Link to="/ncert-mastery" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>� NCERT Mastery</Link></li>
                <li><Link to="/mentorship" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>�‍🏫 Mentorship Program</Link></li>
                <li><Link to="/adhva-vibes-psi" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}> Adhva Vibes - PSI</Link></li>
              </ul>
            )}
          </li>

          {/* Resources Dropdown */}
          <li 
            className="dropdown" 
            onMouseEnter={() => handleMouseEnter('resources')} 
            onMouseLeave={handleMouseLeave}
          >
            <button 
              className="dropdown-toggle" 
              onClick={() => toggleDropdown('resources')}
              data-testid="resources-dropdown"
            >
              📖 Resources <ChevronDown size={16} />
            </button>
            {activeDropdown === 'resources' && (
              <ul 
                className="dropdown-menu"
                onMouseEnter={handleDropdownMenuEnter}
                onMouseLeave={handleDropdownMenuLeave}
              >
                <li><Link to="/resources" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>📝 Study Materials</Link></li>
                <li><Link to="/current-affairs" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>📰 Current Affairs</Link></li>
                <li><Link to="/videos" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>🎥 Video Library</Link></li>
                <li><Link to="/blog" onClick={() => { setIsOpen(false); setActiveDropdown(null); }}>✍️ Blog & Insights</Link></li>
              </ul>
            )}
          </li>

          {/* About - single link (no dropdown) */}
          <li className={isMobile ? 'nav-link-mobile' : ''}>
            <Link 
              to="/about-us" 
              onClick={handleAboutClick}
            >
              ℹ️ About
            </Link>
          </li>

          <li>
            <button 
              className="theme-toggle" 
              onClick={toggleTheme}
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>
          </li>
          
          {user && (
            <>
              <li><Link to="/admin/dashboard" className="btn-secondary" style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }} onClick={() => setIsOpen(false)}>Dashboard</Link></li>
              <li><button className="btn-secondary" onClick={() => { handleLogout(); setIsOpen(false); }} style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}>Logout</button></li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
